import OpenInNew from '@mui/icons-material/OpenInNew'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import TextField from '@mui/material/TextField'
import {
  DataGridPremium,
  GridActionsCellItem,
  type GridApi,
  type GridFilterItem,
  type GridFilterOperator,
  GridToolbarQuickFilter,
  getGridStringOperators,
  Toolbar,
} from '@mui/x-data-grid-premium'
import { CardStatus } from '@mynt/backoffice-api-spec/cards'
import { Access, usePermissions } from 'contexts/permissions'
import { useMemo, useRef } from 'react'
import Spacings from '../../figma/tokens/Spacings'
import FigmaBox from '../../mynt-components/components/FigmaBox'

// The default `contains` filter renders its input at `size="small"`, which is shorter than
// the header filter row's other controls (see `createSelectOperator` below) and looks clipped
// alongside them. Reuse the default filter logic, just with a full-size, height-matched input.
// getGridStringOperators()[0] is always the 'contains' operator.
const containsFilterFn = getGridStringOperators()[0].getApplyFilterFn

const createTextFilterOperator = (label: string): GridFilterOperator => ({
  label,
  value: 'contains',
  getApplyFilterFn: containsFilterFn,
  InputComponent: ({ applyValue, ...params }) => {
    const timerRef = useRef<ReturnType<typeof setTimeout>>()
    const itemRef = useRef(params.item)
    itemRef.current = params.item

    return (
      <Box sx={{ width: '100%', paddingTop: 2 }}>
        <TextField
          fullWidth
          label={label}
          defaultValue={params.item.value ?? ''}
          onChange={(event) => {
            clearTimeout(timerRef.current)
            const value = event.target.value
            timerRef.current = setTimeout(() => applyValue({ ...itemRef.current, value }), 300)
          }}
          onKeyDown={(event) => event.stopPropagation()}
        />
      </Box>
    )
  },
  headerLabel: label,
})

const createSelectOperator = (label: string, options: string[]): GridFilterOperator => ({
  label: label,
  value: 'customArrayContains',
  getApplyFilterFn: (filterItem: GridFilterItem) => {
    const filter = Array.from(filterItem.value)

    if (filter.length === 0) return null

    return (value: unknown) => filter.includes(value)
  },
  InputComponent: ({ applyValue, clearButton, ...params }) => {
    const selected = (params.item.value as string[] | undefined) ?? []
    const labelId = `card-filter-${params.item.field}`

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', paddingTop: 2 }}>
        <FormControl fullWidth sx={{ minWidth: 0 }}>
          <InputLabel id={labelId}>{label}</InputLabel>
          <Select
            labelId={labelId}
            label={label}
            multiple
            value={selected}
            onKeyDown={(event) => event.stopPropagation()}
            onChange={(event) => {
              const value = event.target.value
              applyValue({ ...params.item, value: typeof value === 'string' ? value.split(',') : value })
            }}
            renderValue={(values) => (values.length === 0 ? '' : `${values.length} selected`)}
            sx={{
              // Theme sets `.MuiSelect-multiple` to 8px padding, which is shorter than the
              // 12.5px outlined inputs and drops the label and value below the rest of the row.
              '&& .MuiSelect-select': {
                display: 'flex',
                alignItems: 'center',
                padding: '12.5px 32px 12.5px 12.5px',
              },
            }}
          >
            {options.map((option) => (
              <MenuItem key={option} value={option}>
                <Checkbox checked={selected.includes(option)} size="small" sx={{ pointerEvents: 'none', padding: 0, marginRight: 1 }} />
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {selected.length > 0 ? clearButton : null}
      </Box>
    )
  },
  headerLabel: label,
})

export type Card = {
  owner: string
  number: string
  status: string
  type: string
  variant: string
  category: string
  id: string
  externalId: string
  personId?: string
  address?: string
  singleAmountLimit?: number
}

export type OverviewTableProps = {
  cards: Card[]
  onRowOpen?: (card: Card) => void
  onRowSelect?: (cards: string[]) => void
  onDelete?: (cards: string[]) => void
  onUserInfoCopy?: (rows: Card[]) => void
  selectedRows?: string[]
  initialFilters?: {
    field: string
    value: string | string[]
  }[]
}

const getUniqueOptions = (cards: Card[], field: keyof Card) => Array.from(new Set(cards.map((card) => card[field]))) as string[]

export const CardOverviewTable = ({
  cards,
  onRowOpen,
  onRowSelect,
  onDelete,
  onUserInfoCopy,
  selectedRows,
  initialFilters,
}: OverviewTableProps) => {
  const apiRef = useRef<GridApi>({} as GridApi)

  const statusOptions = Object.values(CardStatus)
  const typeOptions = useMemo(() => getUniqueOptions(cards, 'type'), [cards])
  const variantOptions = useMemo(() => getUniqueOptions(cards, 'variant'), [cards])
  const categoryOptions = useMemo(() => getUniqueOptions(cards, 'category'), [cards])

  const permissions = {
    details: usePermissions('customers.cards.overview.details'),
  }

  return (
    <DataGridPremium
      headerFilters
      disableColumnFilter
      // Without an explicit height, columns with no filter (Owner, Card number, Enfuce ID) fall back to a
      // shorter default row height than columns that do render a filter control, leaving the header
      // filter row visibly uneven.
      headerFilterHeight={81}
      columnVisibilityModel={{ actions: permissions.details }}
      pagination
      checkboxSelection
      disableRowSelectionOnClick
      onRowSelectionModelChange={(model) => {
        // v8 reports select-all as { type: 'exclude', ids } meaning every row
        // except `ids`, so resolve it back to the concrete list of ids.
        const selected =
          model.type === 'exclude'
            ? cards.filter((card) => !model.ids.has(card.id)).map((card) => card.id)
            : Array.from(model.ids, (id) => id.toString())
        onRowSelect?.(selected)
      }}
      onRowDoubleClick={({ row }) => onRowOpen?.(row)}
      initialState={{
        pagination: { paginationModel: { pageSize: 10 } },
        filter: {
          filterModel: {
            items: initialFilters?.map(({ field, value }, index) => ({ id: index, field, value, operator: 'customArrayContains' })) ?? [],
          },
        },
      }}
      rowSelectionModel={selectedRows && { type: 'include', ids: new Set(selectedRows) }}
      pageSizeOptions={[10, 25, 50]}
      apiRef={apiRef}
      rows={cards}
      slots={{
        headerFilterMenu: null,
        toolbar: () => (
          <Toolbar
            render={
              <FigmaBox
                fullPadding
                align="baseline"
                spacing={Spacings.tiny}
                fullWidth
                justify={onDelete ? 'space-between' : 'flex-end'}
                direction="row"
              />
            }
          >
            <FigmaBox align="center" direction="row" gap="2rem">
              <Access permissions="customers.cards.overview.terminate">
                {onDelete && selectedRows && (
                  <FigmaBox>
                    <Button
                      data-testid="delete-cards-button"
                      size="small"
                      disabled={selectedRows.length === 0}
                      onClick={() => onDelete(selectedRows)}
                      variant="contained"
                      color={'error'}
                    >
                      Terminate {selectedRows.length} cards
                    </Button>
                  </FigmaBox>
                )}
              </Access>
              <FigmaBox>
                {onUserInfoCopy && selectedRows && (
                  <Button
                    onClick={() => onUserInfoCopy?.(cards.filter((card) => selectedRows?.includes(card.id)))}
                    variant="tertiary"
                    disabled={selectedRows.length === 0}
                    data-testid="copy-cards-button"
                  >
                    Copy {selectedRows?.length} names and card numbers
                  </Button>
                )}
              </FigmaBox>
            </FigmaBox>
            <FigmaBox>
              <GridToolbarQuickFilter />
            </FigmaBox>
          </Toolbar>
        ),
      }}
      slotProps={{
        toolbar: {
          showQuickFilter: true,
        },
        headerFilterCell: { showClearIcon: true },
      }}
      columns={[
        {
          field: 'owner',
          headerName: 'Owner',
          flex: 1.5,
          filterOperators: [createTextFilterOperator('Owner')],
        },
        {
          field: 'number',
          headerName: 'Card number',
          flex: 0.75,
          filterOperators: [createTextFilterOperator('Card number')],
        },
        {
          field: 'status',
          headerName: 'Status',
          flex: 1.5,
          minWidth: 150,
          filterOperators: [createSelectOperator('Status', statusOptions)],
        },
        {
          field: 'type',
          headerName: 'Type',
          flex: 1.5,
          minWidth: 150,
          filterOperators: [createSelectOperator('Type', typeOptions)],
        },
        {
          field: 'variant',
          headerName: 'Variant',
          flex: 1.5,
          minWidth: 150,
          filterOperators: [createSelectOperator('Variant', variantOptions)],
        },
        {
          field: 'category',
          headerName: 'Category',
          flex: 1.5,
          minWidth: 150,
          filterOperators: [createSelectOperator('Category', categoryOptions)],
        },
        {
          field: 'externalId',
          headerName: 'Enfuce ID',
          flex: 1,
          filterOperators: [createTextFilterOperator('Enfuce ID')],
        },
        {
          field: 'actions',
          type: 'actions',
          getActions: ({ row }) => [<GridActionsCellItem key={1} icon={<OpenInNew />} label="Delete" onClick={() => onRowOpen?.(row)} />],
        },
      ]}
      showToolbar
    />
  )
}
